import {
  makeListArtclesByVisibilityRequest,
  makeListArtclesByCategoryRequest,
  sendRequest,
  eTagToGuid,
  getAccessToken,
  makeSearchArticlesHavingTagsRequest,
  makeSearchArticlesInCategoryRequest,
  makeSearchResourcesHavingTagsRequest,
  makeSearchResourcesInCategoryRequest,
  parseSearchResponse,
  makeGetArticleListItemByIdRequest,
  makeGetResourceListItemByIdRequest,
  makeBatchRequest,
  updateListItemFields,
  publishArticle
} from "."
import createRetryAxios from './axiosRetry'
import { getResourceLibraryFilesByCategory } from "./index"
import { getSiteConfig } from "../../community/siteProvider"

export const listItemsHavingMetadata = async (args, cache) => {
  const {
    category,
    list,
  } = args
  const cacheKey = `listItemsHavingMetadata-${list}-category-${category}`
  const cacheHit = cache.getItem(cacheKey)
  if(cacheHit) return cacheHit

  let value = []
  if(category && list === 'articles') {
    const request = makeListArtclesByCategoryRequest(category)
    const res = await sendRequest(request)
    value = res.value
  }
  if (category && list === 'resources') {
    value = await getResourceLibraryFilesByCategory(category)
  }

  const mapped = value.map(item => {
    const res = {
      title: item.fields.Title || item.fields.LinkFilename,
      id: eTagToGuid(item.eTag),
      listItemId: item.id,
      type: item.contentType.name,
      QSEPortalTags: item.fields.QSEPortalTags,
      QSEPortalResourceCategory: item.fields.QSEPortalResourceCategory,
      QSEPortalOUCornerCategory: item.fields.QSEPortalOUCornerCategory,
      QSEPortalPinPositions: item.fields.QSEPortalPinPositions
    }
    return res
  })

  cache.setItem(cacheKey, mapped)
  return mapped
}

export const searchItemsHavingMetadata = async (args, cache) => {
  const {
    tag,
    category,
    list,
    fetchListItems = false
  } = args

  const cacheKey = `searchItemsHavingMetadata-${list}-${tag ? 'tag' : 'category'}-${tag || category}`
  const cacheHit = cache.getItem(cacheKey)
  let results = []
  if(cacheHit) {
    results = cacheHit
  } else {
    const token = await getAccessToken()
    const options = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
    let request
    if(tag && list === 'articles') request = makeSearchArticlesHavingTagsRequest([tag])
    else if(category && list === 'articles') request = makeSearchArticlesInCategoryRequest(category)
    else if(tag && list === 'resources') request = makeSearchResourcesHavingTagsRequest([tag])
    else if(category && list === 'resources') request = makeSearchResourcesInCategoryRequest(category)
    else throw new Error('metadataLabelInUse args require tag, category, and list props')

    const { baseUrl, path, body, method } = request
    const { data } = await createRetryAxios()[method.toLowerCase()](baseUrl + path, body, options)
    const parsedSearchResponse = parseSearchResponse(data.value)
    results = parsedSearchResponse.results
    cache.setItem(cacheKey, results)
  }

  if(fetchListItems && results.length) {
    const cacheKey2 = `searchItemsHavingMetadata-list-items-${list}-${tag ? 'tag' : 'category'}-${tag || category}`
    const cacheHit2 = cache.getItem(cacheKey2)
    if(cacheHit2) return cacheHit2

    const batchBody = { 
      requests: results.map((item, id) => {
        const listItemRequest =
          list === 'articles' ? makeGetArticleListItemByIdRequest(item.hitId)
            : makeGetResourceListItemByIdRequest(item.hitId)
        return {
          id,
          method: listItemRequest.method,
          url: listItemRequest.path
        }
      })
    }

    const batchRequest = makeBatchRequest(batchBody.requests)
    const { responses } = await sendRequest(batchRequest)
    const mapped = responses.map(item => {
      const res = {
        title: item.body.fields.Title || item.body.fields.LinkFilename,
        id: eTagToGuid(item.body.eTag),
        listItemId: item.id,
        type: item.body.contentType.name,
        QSEPortalTags: item.body.fields.QSEPortalTags,
        QSEPortalResourceCategory: item.body.fields.QSEPortalResourceCategory,
        QSEPortalOUCornerCategory: item.body.fields.QSEPortalOUCornerCategory,
        QSEPortalPinPositions: item.body.fields.QSEPortalPinPositions
      }
      return res
    })
    const filtered = mapped.filter(result => {
      if(tag) return result.QSEPortalTags?.indexOf(`tag:${tag}`) > -1
      if(list === 'articles') return result.QSEPortalOUCornerCategory === category
      if(list === 'resources') return result.QSEPortalResourceCategory === `category:${category}`
      return false
    })
    cache.setItem(cacheKey2, filtered)
    return filtered
  } else {
    return results
  }
}

export const getItemsHavingMetadata = async (args, cache) => {
  if(args.category) {
    const res = await listItemsHavingMetadata(args, cache)
    return res
  }
  if(args.tag) {
    const res = await searchItemsHavingMetadata(args, cache)
    return res
  }
}

export const metadataLabelInUse = async (args, cache) => {
  const results = await searchItemsHavingMetadata(args, cache)
  const inUse = results?.length > 0
  return inUse
}

const retagItem = (tags, oldTag, newTag) => {
  const tagsList = tags.split(',')
  const filteredTags = tagsList.filter(t => t !== oldTag && t !== 'tag:')
  
  if(newTag) {
    filteredTags.push(newTag)
  }
  const deduped = Array.from(new Set(filteredTags));
  const newTags = deduped.join(',')
  return newTags
}

export async function *removeTagFromItems(items, tag, newTag, entityType, cache) {
  const { listIds } = getSiteConfig()
  cache.removeItemsStartingWith('getItemsHavingMetadata')
  cache.removeItemsStartingWith('listItemsHavingMetadata')
  const progress = { completed: 0, total: items.length }
  yield progress
  for(const item of items) {

    const newTags = retagItem(item.QSEPortalTags, `tag:${tag}`, newTag ? `tag:${newTag}` : undefined)
    const listId = entityType === 'articles' ? listIds.pages
      : entityType === 'resources' ? listIds.documentLibrary : undefined
    
    await updateListItemFields(listId, item.id, {
      QSEPortalTags: newTags
    })
    if(entityType === 'articles') {
      await publishArticle(item.id)
    }
    progress.completed += 1
    yield progress
  }
}

export async function *recategorizeItems(items, category, newCategory, entityType, cache) {
  const { listIds } = getSiteConfig()
  cache.removeItemsStartingWith('getItemsHavingMetadata')
  cache.removeItemsStartingWith('listItemsHavingMetadata')
  const progress = { completed: 0, total: items.length }
  yield progress

  const categoryColumn =
    entityType === 'articles' ? 'QSEPortalOUCornerCategory' 
    : entityType === 'resources' ? 'QSEPortalResourceCategory' : ''

  for(const item of items) {
    const listId = entityType === 'articles' ? listIds.pages
      : entityType === 'resources' ? listIds.documentLibrary : undefined
    
    const optionalPrefix = entityType === 'resources' ? 'category:' : ''
    const payload = {
      [categoryColumn]: optionalPrefix + newCategory
    }

    if(item.QSEPortalPinPositions) {
      payload.QSEPortalTags = retagItem(item.QSEPortalTags, `visibility:OUCorner::${category}`, `visibility:OUCorner::${newCategory}`)
      const pinPositions = item.QSEPortalPinPositions?.split(',')
      const newPinPositions = pinPositions.filter(pinPosition => pinPosition.indexOf(`pinned:OUCorner:${category}`) === -1)
      payload.QSEPortalPinPositions = newPinPositions.join(',')
    }

    await updateListItemFields(listId, item.id, payload)
    if(entityType === 'articles') {
      await publishArticle(item.id)
    }
    progress.completed += 1
    yield progress
  }
}